import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Modal from './Modal';
import GoogleSignIn from './GoogleLogin';
import api from '../api';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '../constants';

/**
 * Login and sign-up as dialogs over the landing page.
 *
 * The landing page is the pitch; sending someone to a separate `/login` screen
 * to answer it throws the pitch away and makes them find their way back. Both
 * forms therefore open in place, over the page they were convinced by, and the
 * switch between them is inside the same dialog rather than a navigation.
 *
 * Copy here is English on purpose: the landing page it opens over is English,
 * and a Turkish form inside an English page reads as a different product. The
 * signed-in app stays bilingual — this is the one surface that is not.
 *
 * The flows themselves are the existing ones, unchanged: `/api/token` for
 * login, and the register endpoint's 200/201/208 contract for sign-up, where
 * 200 means "a verification code is on its way" and 208 means "you already
 * have one, enter it".
 */

const RESEND_SECONDS = 60;

function Errors({ items }) {
    if (!items.length) return null;
    return (
        <ul className="list-none p-0 m-0 space-y-1">
            {items.map((message, index) => (
                <li key={index} className="text-sm text-red-600 leading-snug">{message}</li>
            ))}
        </ul>
    );
}

function SubmitButton({ loading, children }) {
    return (
        <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-bold
                bg-gradient-to-r from-primary-500 to-primary-700 shadow-[0_8px_24px_-8px_rgba(32,201,181,0.8)]
                hover:shadow-[0_12px_30px_-8px_rgba(32,201,181,0.9)] hover:-translate-y-px
                active:translate-y-0 transition disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
        >
            {loading ? <span className="spinner-sm" /> : children}
        </button>
    );
}

function Divider() {
    return (
        <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400 font-medium">or</span></div>
        </div>
    );
}

function LoginForm({ onDone }) {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);

    const submit = async (event) => {
        event.preventDefault();
        setErrors([]);
        setLoading(true);
        try {
            const res = await api.post('/api/token', { username, password });
            localStorage.setItem(ACCESS_TOKEN, res.data.access);
            localStorage.setItem(REFRESH_TOKEN, res.data.refresh);
            onDone?.();
            navigate('/profile');
        } catch (error) {
            // A network failure has no `response` at all, and the old form
            // threw on that path instead of saying anything.
            const data = error.response?.data;
            const detail = data?.non_field_errors || data?.detail;
            setErrors([
                Array.isArray(detail) ? detail[0]
                    : detail || 'Wrong username or password. Please try again.',
            ]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-3">
            <input className="input" type="text" value={username} autoComplete="username"
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username or email" maxLength={30} required />
            <input className="input" type="password" value={password} autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password" maxLength={128} required />

            <Errors items={errors} />

            <SubmitButton loading={loading}>Log in</SubmitButton>

            <div className="text-center">
                <Link to="/reset_password" className="text-xs font-semibold text-slate-500 hover:text-primary-700 transition">
                    Forgot your password?
                </Link>
            </div>

            <Divider />
            <GoogleSignIn method="login" />
        </form>
    );
}

function SignupForm({ onDone }) {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConf, setPasswordConf] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [code, setCode] = useState('');
    const [codeStage, setCodeStage] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [notice, setNotice] = useState('');
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        if (countdown <= 0) return undefined;
        const timer = setInterval(() => setCountdown((value) => value - 1), 1000);
        return () => clearInterval(timer);
    }, [countdown]);

    const readErrors = (data = {}) => {
        const collected = [];
        ['username', 'email', 'password', 'password_conf', 'code', 'non_field_errors'].forEach((field) => {
            const value = data[field];
            if (!value) return;
            (Array.isArray(value) ? value : [value]).forEach((message) => collected.push(message));
        });
        setErrors(collected.length ? collected : ['Something went wrong. Please try again.']);
    };

    const submit = async (event) => {
        event.preventDefault();
        // Belt and braces: the checkbox is `required`, but a form can be
        // submitted programmatically and consent is the one thing here we may
        // have to prove we asked for.
        if (!accepted) {
            setErrors(['Please accept the Terms of Use and Privacy Policy to continue.']);
            return;
        }
        setErrors([]);
        setNotice('');
        setLoading(true);
        try {
            const res = await api.post('/api/user/register/', {
                username, password, email, validate_code: code, password_conf: passwordConf,
            });

            if (res.status === 201) {
                const tokens = await api.post('/api/token', { username, password });
                localStorage.setItem(ACCESS_TOKEN, tokens.data.access);
                localStorage.setItem(REFRESH_TOKEN, tokens.data.refresh);
                onDone?.();
                navigate('/profile');
                return;
            }

            if (res.status === 200) {
                // Awaited, not fired and forgotten: a code that never left the
                // server must not flip the form to "enter your code".
                await api.post('api/CreateVCode/', { email });
                setNotice('We sent a 6-digit code to your inbox.');
                setCodeStage(true);
                setCountdown(RESEND_SECONDS);
                return;
            }

            if (res.status === 208) setCodeStage(true);
        } catch (error) {
            readErrors(error.response?.data);
        } finally {
            setLoading(false);
        }
    };

    const resend = async () => {
        setErrors([]);
        try {
            await api.post('api/CreateVCode/', { email });
            setNotice('We sent a new code to your inbox.');
            setCountdown(RESEND_SECONDS);
        } catch (error) {
            readErrors(error.response?.data);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-3">
            <input className="input" type="text" value={username} autoComplete="username"
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username — this becomes your astreona.com link" maxLength={30} required />
            <input className="input" type="email" value={email} autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address" maxLength={254} required />
            <input className="input" type="password" value={password} autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password" pattern="\S+" title="Password cannot contain spaces"
                minLength={8} maxLength={128} required />
            <input className="input" type="password" value={passwordConf} autoComplete="new-password"
                onChange={(e) => setPasswordConf(e.target.value)}
                placeholder="Confirm password" pattern="\S+" title="Password cannot contain spaces"
                minLength={8} maxLength={128} required />
            {/* Invite code is UI only for now: the register endpoint has no
                notion of it yet, so it stays out of the payload above until
                the backend does. Optional, so it never blocks a sign-up. */}
            <input className="input" type="text" value={inviteCode} autoComplete="off"
                onChange={(e) => setInviteCode(e.target.value.trim())}
                placeholder="Invite code (optional)" spellCheck={false} maxLength={32} />

            {codeStage && (
                <div className="space-y-2 p-3.5 bg-primary-50 rounded-xl border border-primary-100">
                    <div className="flex gap-2">
                        <input className="input flex-1 tracking-[0.3em] font-bold text-center"
                            value={code} onChange={(e) => setCode(e.target.value)}
                            placeholder="000000" inputMode="numeric" pattern="\d{6}" maxLength={6} />
                        <button type="button" onClick={resend} disabled={countdown > 0}
                            className="btn-secondary text-sm whitespace-nowrap px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed">
                            {countdown > 0 ? `${countdown}s` : 'Resend'}
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                        Not in your inbox? Check the spam folder and mark it as not spam.
                    </p>
                </div>
            )}

            {/* Consent is a ticked box, not a sentence under the button. The
                documents cover escrow, fees, chargebacks and what happens to a
                balance — "by continuing you agree" is the weakest way to ask
                about any of that, and the hardest to show was ever asked. Both
                links open in a new tab so a half-filled form survives reading
                them. */}
            <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 cursor-pointer hover:border-primary-300 transition">
                <input type="checkbox" required checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 flex-shrink-0 accent-primary-600 cursor-pointer" />
                <span className="text-[12px] text-slate-600 leading-relaxed">
                    I have read and accept the{' '}
                    <a href="/termsofuse" target="_blank" rel="noopener noreferrer"
                        className="font-semibold text-primary-700 hover:underline">Terms of Use</a>
                    {' '}and the{' '}
                    <a href="/privacypolicy" target="_blank" rel="noopener noreferrer"
                        className="font-semibold text-primary-700 hover:underline">Privacy Policy</a>,
                    and I am at least 18 years old.
                </span>
            </label>

            {notice && <p className="text-sm text-primary-700 font-medium">{notice}</p>}
            <Errors items={errors} />

            <SubmitButton loading={loading}>{codeStage ? 'Verify and create account' : 'Create free account'}</SubmitButton>

            <Divider />
            {/* Google sign-up creates an account in one click, so it has to sit
                behind the same consent as the form above it — otherwise the
                checkbox is a formality anyone can walk around. The widget is a
                Google-rendered iframe and cannot be disabled, so it is not
                mounted until the box is ticked. */}
            {accepted ? <GoogleSignIn method="register" /> : (
                <p className="mt-4 text-center text-[12px] text-slate-400 leading-relaxed">
                    Accept the Terms of Use and Privacy Policy above to continue with Google.
                </p>
            )}
        </form>
    );
}

/**
 * @param {'login'|'signup'|null} view  which form to show; `null` closes
 * @param {(view: 'login'|'signup'|null) => void} onView  switch or close
 */
function AuthModal({ view, onView }) {
    if (!view) return null;
    const isLogin = view === 'login';

    return (
        <Modal
            open
            size="md"
            onClose={() => onView(null)}
            title={isLogin ? 'Welcome back' : 'Start earning on Astreona'}
            subtitle={isLogin
                ? 'Log in to your dashboard, campaigns and payouts.'
                : 'Free to join. You only pay a fee when you actually earn.'}
            footer={
                <p className="text-sm text-slate-500 text-center">
                    {isLogin ? "Don't have an account yet?" : 'Already have an account?'}{' '}
                    <button type="button" onClick={() => onView(isLogin ? 'signup' : 'login')}
                        className="font-bold text-primary-700 hover:text-primary-800 transition">
                        {isLogin ? 'Sign up' : 'Log in'}
                    </button>
                </p>
            }
        >
            {isLogin ? <LoginForm onDone={() => onView(null)} /> : <SignupForm onDone={() => onView(null)} />}
        </Modal>
    );
}

export default AuthModal;
